import {
    ERROR_DESC_CANNOT_SEND_GROUP_MSG,
    ERROR_TITLE_SORRY
} from "@/constants/errors";
import { SendHorizonal } from "lucide-react";
import {
  selectConversations,
  selectCurrConversation,
  selectKeyExchangeVersion,
  selectKeyExchangeVersions,
  keyVersionForAddress,
  selectEncryptionEnabled,
  selectGroupEncryptionEnabled,
  setConversationEncryptionReady,
} from "@/features/control/controlSlice";
import {
  selectHasUndecryptedMessages,
  selectMessagesLoading,
  sendMessage,
  setReloadMessages,
} from "@/features/chat/chatSlice";
import { clearDraft, selectDraft, setDraft } from "@/features/chat/draftsSlice";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CHAT_MSG_TEXTAREA_LOADING_PLACEHOLDER,
  CHAT_MSG_TEXTAREA_PLACEHOLDER,
  EMAIL_CONVERSATION_NOTICE,
} from "@/constants/ui";
import { Conversation } from "@/types/conversation";
import FileUploadDialog from "./FileUploadDialog";
import { Textarea } from "@/components/ui/textarea";
import { WEBMSG_API_GROUP_MSG_SEND_ERROR } from "../../constants/errors";
import { useToast } from "@/components/ui/use-toast";
import { useEncryption } from "@/hooks/useEncryption";
import { refreshPublicKey, wasRefreshedThisSession } from "@/services/keyExchangeService";
import { getGroups } from "@/services/userGroupService";
import { requestGroupKey } from "@/services/groupKeyService";
import { isEmailAddress, isExternalDmAddress } from "@/utils/addresses";

const ChatInputs = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const messagesLoading = useAppSelector(selectMessagesLoading);
  const userId = useAppSelector((state) => state.auth.userId);
  const username = useAppSelector((state) => state.auth.username);
  const keyExchangeVersion = useAppSelector(selectKeyExchangeVersion);
  // Per-address key versions (the global counter re-runs checkEncryption; these gate the
  // reload). In a ref so the post-await gate reads the value current at decision time.
  const keyExchangeVersions = useAppSelector(selectKeyExchangeVersions);
  const keyExchangeVersionsRef = useRef(keyExchangeVersions);
  keyExchangeVersionsRef.current = keyExchangeVersions;
  const encryptionEnabled = useAppSelector(selectEncryptionEnabled);
  const groupEncryptionEnabled = useAppSelector(selectGroupEncryptionEnabled);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const [canEncrypt, setCanEncrypt] = useState<boolean>(false);
  // Shown when an encrypted group send is attempted without a usable symmetric key —
  // we request the key from the owner and block the send rather than leaking plaintext.
  const [showMissingKeyDialog, setShowMissingKeyDialog] = useState<boolean>(false);
  const { encrypt, encryptForGroup, canEncryptFor, canEncryptGroup, setupGroupEncryption, status } = useEncryption();
  // Mutable ref so async functions inside checkEncryption can read the current
  // isInitialized value without being stuck on a stale closure capture.
  // Updated on every render (safe because refs are mutable outside effects).
  const isInitializedRef = useRef(status.isInitialized);
  isInitializedRef.current = status.isInitialized;
  // Whether the current view still holds any 🔒 message. Read inside the async
  // checkEncryption to decide if a reload is worthwhile — a fully-decrypted view
  // (the common cached case) needs no reload, avoiding a full re-fetch + attachment
  // re-pull. Kept in a ref so the post-await decision reads the current value.
  const hasUndecrypted = useAppSelector(selectHasUndecryptedMessages);
  const hasUndecryptedRef = useRef(hasUndecrypted);
  hasUndecryptedRef.current = hasUndecrypted;
  // Whether the current conversation is a group — read in the keyExchangeVersion
  // effect without adding it to the dep array (avoids extra effect runs).
  const isGroupConversationRef = useRef(false);
  const currConv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
  isGroupConversationRef.current = currConv?.type === "Group";
  // Sending is blocked until the history loads: a send mid-load errors out, and its optimistic
  // row is wiped by the arriving fetch. No enabled gap on a switch (loadMessages is dispatched
  // in the same tick the pane clears); cache hits and background refreshes leave `loading`
  // false, so neither blocks.
  const isConversationLoading = currConv == null || messagesLoading;
  // Replies go to the router's configured reply-to, not back here — the only inbound messages
  // are its own delivery notices. Surfaced above the composer, where it's read in time to matter.
  const isEmailConversation = currConv != null && isEmailAddress(currConv.address);
  // The draft belongs to the conversation, not to this component (see draftsSlice), so
  // switching conversations shows that conversation's own unsent text.
  const draftAddress = currConv?.address ?? "";
  const message = useAppSelector((state) => selectDraft(state, draftAddress));
  // Tracks the last address for which we attempted a key fetch.
  const lastKeyFetchAddressRef = useRef<string | null>(null);
  // Tracks the last address for which the 1:1 key fetch SUCCEEDED. Used to break
  // the infinite-fetch loop: requestPublicKey → @keys WebSocket response →
  // keyExchangeReceived() → keyExchangeVersion++ → lastKeyFetchAddressRef reset
  // → requestPublicKey again → … The reset must be suppressed once we have the key.
  // NOTE: intentionally NOT set for group conversations — group owners should
  // re-run setupGroupEncryption on each keyExchangeVersion bump so that newly
  // arrived member keys immediately trigger key distribution.
  const keyFetchedForAddressRef = useRef<string | null>(null);
  // Tracks the address currently being fetched. Prevents the keyExchangeVersion
  // reset effect from clearing lastKeyFetchAddressRef while a fetch is in-flight
  // (own sendkeys WebSocket echo increments keyExchangeVersion before the HTTP
  // response resolves, which would otherwise trigger a second fetch).
  const keyFetchInFlightRef = useRef<string | null>(null);
  // Per-address key version at which each conversation last had its key-ready reload —
  // a Map (not a single last-address ref) so A↔B alternation doesn't re-trigger reloads.
  const reloadedAtVersionRef = useRef<Map<string, number>>(new Map());
  // Tracks when setupGroupEncryption last completed per group address. Used to
  // absorb distribution echoes: each distributed-to member triggers a
  // keyExchangeVersion bump, which would otherwise immediately re-trigger setup.
  const groupSetupLastRunAtRef = useRef<Map<string, number>>(new Map());

  // Reset the attempt guard when a key exchange event fires (e.g. peer just shared
  // their key), but ONLY if no fetch is in-flight and the address hasn't already
  // been successfully fetched. Without this guard, our own requestPublicKey call
  // triggers a WebSocket echo → keyExchangeReceived → resets the ref →
  // requestPublicKey again → loop.
  // For group conversations the "fetch succeeded" guard is intentionally skipped so
  // that setupGroupEncryption re-runs whenever a member's key exchange event fires —
  // this ensures newly arrived member keys immediately receive their key distribution.
  // Long enough to absorb all per-member distribution echoes (each bump arrives
  // within a few seconds of each other), short enough that a member who comes
  // online later will still trigger a re-run.
  const GROUP_SETUP_ECHO_COOLDOWN_MS = 30_000;
  useEffect(() => {
    const addr = lastKeyFetchAddressRef.current;
    const lastRunAt = isGroupConversationRef.current && addr
      ? (groupSetupLastRunAtRef.current.get(addr) ?? 0)
      : 0;
    const blocked =
      addr !== null &&
      (keyFetchInFlightRef.current === addr ||   // fetch in-flight
        (!isGroupConversationRef.current && keyFetchedForAddressRef.current === addr) || // 1:1 only: suppress loop after success
        Date.now() - lastRunAt < GROUP_SETUP_ECHO_COOLDOWN_MS); // group setup ran recently — absorb echoes
    if (!blocked) {
      lastKeyFetchAddressRef.current = null;
    }
  }, [keyExchangeVersion]);

  // Check if encryption is available for the current conversation.
  // For 1:1: fetch the recipient's public key from @keys if not cached.
  //   The key fetch starts as soon as userId is available — it does NOT wait for
  //   status.isInitialized. The HTTP round-trip to @keys doesn't need the own key,
  //   so fetching early means the peer key is in IndexedDB by the time own-key init
  //   completes and the ChatMessages reload fires.
  // For groups: if current user is the owner, generate and distribute the key.
  useEffect(() => {
    const checkEncryption = async () => {
      if (currConversationIndex === null || !userId) {
        setCanEncrypt(false);
        return;
      }

      const conversation = conversations[currConversationIndex];
      if (!conversation) {
        setCanEncrypt(false);
        return;
      }

      if (conversation.type === "Group") {
        const groupAddress = conversation.address;
        const groupName = conversation.groupName || "";

        // If the current user is the group owner, generate + distribute the key.
        // If a non-owner, send a group_key_request to the owner if we lack the key.
        // ownerName may be absent after page reload (GetConversations doesn't return it),
        // so fall back to the ownerName from the groups list which always has it.
        if (username && lastKeyFetchAddressRef.current !== groupAddress) {
          try {
            const groups = await getGroups();
            const group = groups.find((g) => g.address === groupAddress || g.name === groupName);
            const ownerName = conversation.ownerName || group?.ownerName || "";
            if (group && username === ownerName) {
              // Owner: generate + distribute the key. Distribution needs our own EC keypair
              // (distributeGroupKeyToMembers no-ops without it), so defer until init. Don't
              // set lastKeyFetchAddressRef while deferring — leaving it unset lets this effect
              // (dep: status.isInitialized) re-run and distribute once the keypair is ready.
              if (isInitializedRef.current) {
                // keyFetchInFlightRef blocks the keyExchangeVersion effect from
                // resetting lastKeyFetchAddressRef while distribution is running
                // (each distributed-to member echo would otherwise re-trigger setup).
                // groupSetupLastRunAtRef absorbs echoes that arrive after the await
                // resolves (before keyFetchInFlightRef is cleared they're blocked too).
                lastKeyFetchAddressRef.current = groupAddress;
                keyFetchInFlightRef.current = groupAddress;
                try {
                  await setupGroupEncryption(groupAddress, groupName, ownerName, group.members);
                  groupSetupLastRunAtRef.current.set(groupAddress, Date.now());
                } finally {
                  keyFetchInFlightRef.current = null;
                }
              }
            } else if (group && ownerName && userId) {
              // Non-owner: request the key from the owner if we lack a current one (missing or
              // stale). requestGroupKey dedups, so re-running on each key event is harmless.
              // Matches Android's DefaultGroupKeyExchangeManager.getAllSymmetricKeyRequests().
              // The owner's key is refreshed on the first open per session (manual sync covers
              // later rotations); requestGroupKey dedups, so key-event re-runs are harmless.
              lastKeyFetchAddressRef.current = groupAddress;
              if (!(await canEncryptGroup(groupAddress, true))) {
                await requestGroupKey(groupAddress, username, Number(userId), groupName);
              }
            }
          } catch (err) {
            console.error("[GroupKey] Failed to set up group encryption:", err);
          }
        }

        const hasKey = await canEncryptGroup(groupAddress);
        // Gate on init like the 1:1 path: encryptForGroup throws without our own keypair, so
        // advertising "encrypted" before init would let a send fall back to PLAINTEXT. The
        // effect re-runs when isInitialized flips. (Reload below only needs the key.)
        const canEncryptNow = hasKey && isInitializedRef.current;
        setCanEncrypt(canEncryptNow);
        dispatch(setConversationEncryptionReady(canEncryptNow));

        // Reload once the group key arrives so existing 🔒 rows decrypt — only while the
        // view still has retryable rows and THIS group's key version moved. Mirrors 1:1 below.
        const groupKeyVersion = keyVersionForAddress(keyExchangeVersionsRef.current, groupAddress);
        if (
          hasKey &&
          hasUndecryptedRef.current &&
          reloadedAtVersionRef.current.get(groupAddress) !== groupKeyVersion
        ) {
          reloadedAtVersionRef.current.set(groupAddress, groupKeyVersion);
          dispatch(setReloadMessages(true));
        }
        return;
      }

      // No external gateway is encryptable — federation carries the body through as sent (E2EE
      // there is still a TODO) and email hands it to SMTP. `public_keys` is per-router besides,
      // so a `getkeys` could only error. Fall through to plaintext without spending the
      // round-trip on a key that can't exist.
      if (isExternalDmAddress(conversation.address)) {
        setCanEncrypt(false);
        dispatch(setConversationEncryptionReady(false));
        return;
      }

      // 1:1 conversation — fetch peer key without waiting for own-key init.
      const recipientUsername = conversation.address;
      let hasKey = await canEncryptFor(recipientUsername);

      if (lastKeyFetchAddressRef.current !== conversation.address) {
        lastKeyFetchAddressRef.current = conversation.address;
        keyFetchInFlightRef.current = conversation.address;
        try {
          // First open per session only — later peer rotations are caught by a failed
          // decrypt (evidence) or the manual sync, never by an @keys fetch per message.
          if (!wasRefreshedThisSession(conversation.address)) {
            await refreshPublicKey(conversation.address, Number(userId));
          }
          hasKey = await canEncryptFor(recipientUsername);
          if (hasKey) {
            // Mark as successfully fetched so the keyExchangeVersion reset effect
            // won't re-trigger a fetch loop on the WebSocket echo from @keys.
            keyFetchedForAddressRef.current = conversation.address;
          }
        } catch (err) {
          console.error("[E2EE] Could not fetch public key for", conversation.address, err);
        } finally {
          keyFetchInFlightRef.current = null;
        }
      }

      // Use the ref here (not the closure-captured status.isInitialized) so that
      // if isInitialized flips while requestPublicKey was in-flight we still read
      // the current value rather than the stale one from when the effect started.
      const isReady = hasKey && isInitializedRef.current;

      // Reload once both keys are ready — only while the view still has retryable 🔒 rows
      // and THIS peer's key version moved (otherwise it re-runs the same failed decrypts
      // and re-pulls every attachment).
      const peerKeyVersion = keyVersionForAddress(keyExchangeVersionsRef.current, conversation.address);
      if (
        isReady &&
        hasUndecryptedRef.current &&
        reloadedAtVersionRef.current.get(conversation.address) !== peerKeyVersion
      ) {
        reloadedAtVersionRef.current.set(conversation.address, peerKeyVersion);
        dispatch(setReloadMessages(true));
      }

      // Outbound encryption requires both peer key and own key to be ready.
      setCanEncrypt(isReady);
    };

    checkEncryption();
  }, [currConversationIndex, conversations, canEncryptFor, canEncryptGroup, setupGroupEncryption, status.isInitialized, userId, username, keyExchangeVersion]);

  // Send-time guard: a member without a current key asks the owner instead of the message
  // going out in plaintext.
  const requestGroupKeyFromOwner = (conversation: Conversation) => {
    if (!userId || !username) return;
    void requestGroupKey(conversation.address, username, Number(userId), conversation.groupName);
  };

  // Helper function for sending a message through the file upload dialog
  const handleSendTextMessage = async (forcePlaintext = false) => {
    // The loading guard also covers "Send anyway": a reload can start while that dialog is open.
    if (message.trim().length === 0 || currConversationIndex === null || isConversationLoading) {
      return;
    }

    const conversation = conversations[currConversationIndex];
    const { addressId, address, addressType } = conversation;
    const isGroup = conversation.type === "Group";

    // Group send-time guard: canEncrypt state can lag the real key, so re-check on send
    // (one IDB read). If E2EE is wanted for this group but we lack a usable key, warn the user
    // (draft preserved) so they can cancel or send unencrypted. forcePlaintext skips the guard —
    // the user already chose "send anyway".
    if (!forcePlaintext && isGroup && routerE2eeEnabled && groupEncryptionEnabled) {
      const groupReady = (await canEncryptGroup(address)) && status.isInitialized;
      if (!groupReady) {
        void requestGroupKeyFromOwner(conversation);
        setShowMissingKeyDialog(true);
        return;
      }
    }

    let messageToSend = message;
    // Groups respect groupEncryptionEnabled toggle; 1:1 messages respect encryptionEnabled.
    // forcePlaintext (user chose "send anyway") sends without encryption.
    const shouldEncrypt = !forcePlaintext && routerE2eeEnabled &&
      (isGroup ? groupEncryptionEnabled : canEncrypt && encryptionEnabled);

    let encrypted = false;
    if (shouldEncrypt) {
      try {
        if (isGroup) {
          messageToSend = await encryptForGroup(message, address, conversation.groupName || "");
        } else {
          messageToSend = await encrypt(message, address);
        }
        encrypted = true;
      } catch (error) {
        if (isGroup) {
          // Encryption unexpectedly failed — warn (draft preserved) so the user can cancel or
          // choose to send unencrypted, rather than silently leaking plaintext.
          console.error("Group encryption failed unexpectedly:", error);
          void requestGroupKeyFromOwner(conversation);
          setShowMissingKeyDialog(true);
          return;
        }
        console.error("Encryption failed, sending as plaintext:", error);
        messageToSend = message;
        toast({
          variant: "destructive",
          title: "Encryption Failed",
          description: "Could not encrypt message — sending as plaintext.",
        });
      }
    }

    // Empty the box as the send starts (what the old isSending effect did), but only for this
    // conversation, and only once every guard above has passed — the missing-key and
    // encryption-failure paths return early and keep the draft.
    dispatch(clearDraft(address));

    const msgResponse = await dispatch(
      sendMessage({
        address,
        addressId,
        addressType,
        message: messageToSend,
        displayMessage: encrypted ? message : undefined,
      })
    );
    

    // TODO: potentially show toast for other send errors
    // Show toast if there is an error response (for groups)
    if (sendMessage.rejected.match(msgResponse)) {
      const errorMessage = msgResponse.error.message;
      console.error(errorMessage);

      if (errorMessage === WEBMSG_API_GROUP_MSG_SEND_ERROR) {
        return toast({
          variant: "destructive",
          title: ERROR_TITLE_SORRY,
          description: ERROR_DESC_CANNOT_SEND_GROUP_MSG,
        });
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendTextMessage();
    }
  };

  return (
    <div className="flex flex-col w-full mt-2">
      {isEmailConversation && (
        <p className="px-1 pb-1 text-xs text-neutral-500">
          {EMAIL_CONVERSATION_NOTICE}
        </p>
      )}

      <div className="flex w-full gap-2">
        <FileUploadDialog disabled={isConversationLoading} />
        <Textarea
          placeholder={
            isConversationLoading
              ? CHAT_MSG_TEXTAREA_LOADING_PLACEHOLDER
              : CHAT_MSG_TEXTAREA_PLACEHOLDER
          }
          className="resize-none"
          rows={1}
          onChange={(e) => dispatch(setDraft({ address: draftAddress, text: e.target.value }))}
          onKeyDown={handleKeyDown}
          value={message}
          disabled={isConversationLoading}
        />

        <Button
          disabled={isConversationLoading || message.trim().length === 0}
          onClick={() => handleSendTextMessage()}
        >
          <SendHorizonal size={20} />
        </Button>

        <AlertDialog open={showMissingKeyDialog} onOpenChange={setShowMissingKeyDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Encryption key not ready</AlertDialogTitle>
              <AlertDialogDescription>
                We don't have this group's encryption key from the owner yet, so this message
                can only be sent unencrypted. Cancel to keep your draft and try again once the
                key arrives, or send it unencrypted now.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowMissingKeyDialog(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowMissingKeyDialog(false);
                  void handleSendTextMessage(true);
                }}
              >
                Send anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default ChatInputs;
